// DEPENDENCIES
import { FormikValues } from "formik";

// TYPES
import {
  GenderInitials,
  IdentityType,
  IdentityVersion,
  Option,
  PersonType,
  PitbullProvinces,
  Step,
  StepOnePJ,
} from "../../types";

// UTILS
import {
  addressFields,
  clearAddressOf,
  fieldsFrom,
  pepFields,
  spouseFields,
} from "../../inputs";
import {
  mediumSelectedAddressRequired,
  multipleSelectRequired,
  radioOrSingleSelectRequired,
} from "../../validations";
import {
  fuseProvince,
  parseDate,
  parseGenderFromInitial,
  buildFullAddress,
} from "../../app/utils";
import { parseStateAddress } from "../../utils";
import { getDraftReferrer } from "../../utils/draftBase";
import { getCompanyData } from "../../utils/companies";
import { sendStep } from "../../connectors/connectors";
import { addSendedStep } from "../../reducers/onboardingDataReducer";

const representativeStep = (
  countriesOptions: Option[],
  activitiesOptions: Option[],
  citizenshipOptions: Option[],
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces,
  provinces: PitbullProvinces,
): Omit<Step, "step"> => {
  const provincesOption = provinces.map((prov) => ({
    value: prov.name,
    label: prov.name,
  }));

  return {
    name: "Datos del representante",
    titleOfStep: "Representante",
    disableButtonBack: true,
    subSteps: [
      {
        step: 1,
        fields: fieldsFrom(
          ["name", { disabled: true }],
          ["lastName", { disabled: true }],
          [
            "citizenship",
            {
              options: citizenshipOptions,
              validation: radioOrSingleSelectRequired(citizenshipOptions),
            },
          ],
          "birthDate",
          "civilStatus",
          ...spouseFields(),
          ...pepFields(),
          "telephone",
          [
            "activities",
            {
              validation: multipleSelectRequired(activitiesOptions),
              options: activitiesOptions,
            },
          ],
          "activitiesDetails",
        ),
      },
      {
        step: 2,
        fields: addressFields("representativesAddresses", {
          "address.country": {
            options: countriesOptions,
            validation: radioOrSingleSelectRequired(countriesOptions),
            onChange: clearAddressOf("representativesAddresses"),
          },
          "address.fullAddress": {
            validation: mediumSelectedAddressRequired,
            autoSelect: "representativesAddresses.fullAddress",
            country: "representativesAddresses.country",
            formatInPreConfirmation: (value) =>
              value ? value?.split(",")?.[0] : "-", // Only show street
            onChange: (values, setFieldValue) => {
              const {
                formattedAddress: {
                  postalCode: zipCode,
                  state,
                  city,
                  municipality,
                  street,
                  streetNumber: number,
                  country,
                },
              } = values;

              const provinces = getProvincesFilteredByCountry(country);

              const fusedProvince = fuseProvince(provinces, state, city);

              //// If no records are found in FUSE, we set the first province by default to avoid breaking
              const finalProvince = fusedProvince || provinces[0].name;

              setFieldValue("representativesAddresses.zipCode", zipCode);
              setFieldValue("representativesAddresses.state", finalProvince);
              setFieldValue("representativesAddresses.city", city);
              setFieldValue(
                "representativesAddresses.municipality",
                municipality,
              );
              setFieldValue("representativesAddresses.street", street);
              setFieldValue("representativesAddresses.number", number);

              const newFullAddress = buildFullAddress(
                street,
                number,
                city,
                municipality,
              );

              setFieldValue(
                "representativesAddresses.fullAddress",
                newFullAddress,
              );
            },
          },
          "address.floor": { maxLength: 10 },
          "address.apartment": { maxLength: 10 },
          "address.state": {
            validation: radioOrSingleSelectRequired(provincesOption),
            dynamicOptions: (formik) => {
              const value =
                formik.values.representativesAddresses?.country || "Argentina";

              //Filter provinces by country
              const provinces = getProvincesFilteredByCountry(
                value || undefined,
              );

              const provincesOption = provinces.map((prov) => ({
                value: prov.name,
                label: prov.name,
              }));

              return provincesOption;
            },
          },
        }),
        onContinue: async (values: FormikValues, dispatch, state) => {
          if (!state || !dispatch)
            throw new Error("Error: state or dispatch not found");

          const {
            email,
            name,
            lastName,
            gender,
            cuit,
            identityDocument,
            tramitNumber,
            citizenship,
            civilStatus,
            telephone,
            birthDate,
            birthCountry,
            pep,
            spouseCuil,
            spouseLastName,
            spouseName,
            activities,
            activitiesDetails,
            representativesAddresses: {
              floor,
              apartment,
              state: stateAddress,
              city,
              street,
              number,
              municipality,
              country,
              zipCode,
            },
          } = values;

          const stepOne = state.onboardingData?.sendedSteps?.find(
            (step) => step["@type"] === "1_JURIDICAL",
          ) as StepOnePJ;

          const versionOfIdentity =
            state.renaper.copy ||
            stepOne?.representativeInformation?.identityDocument?.version;

          const creationDate = state.renaper.creationDate
            ? parseDate(state.renaper.creationDate)
            : stepOne?.representativeInformation?.identityDocument?.creationDate;

          const expirationDate = state.renaper.expirationDate
            ? parseDate(state.renaper.expirationDate)
            : stepOne?.representativeInformation?.identityDocument?.expirationDate;

          const procedureId = state.renaper.procedureId || tramitNumber;

          const representativeCuit =
            cuit ||
            state.authentication.draftBase?.cuit ||
            stepOne?.representativeInformation?.cuit;

          const step = {
            "@type": "1_JURIDICAL",
            representativeInformation: {
              name,
              lastName,
              cuit: representativeCuit,
              email,
              gender: parseGenderFromInitial[gender as GenderInitials],
              identityDocument: {
                type: "DNI" as IdentityType,
                idNumber: identityDocument,
                procedureId,
                version: versionOfIdentity as IdentityVersion,
                creationDate,
                expirationDate,
              },
              telephone,
              citizenship,
              birthCountry,
              birthDate: parseDate(birthDate),
              civilStatus,
              pep,
              pepDetails: pep === "NONE" ? undefined : values.pepDetails,
              addresses: [
                {
                  street,
                  number,
                  zipCode,
                  city,
                  state: parseStateAddress(stateAddress) || "",
                  country,
                  floor,
                  apartment,
                  municipality,
                },
              ],
              activities: activities,
              activitiesDetails:
                activitiesDetails !== "" ? activitiesDetails : null,
              spouse:
                civilStatus === "MARRIED"
                  ? {
                      lastName: spouseLastName,
                      name: spouseName,
                      ...(spouseCuil && { cuit: spouseCuil }),
                    }
                  : undefined,
            },
            referrer: getDraftReferrer(values),
          };

          const body = {
            companyType: getCompanyData().companyName,
            personType: state.activeTemplate.onboardingType as PersonType,
            email,
            steps: [step],
          };
          await sendStep({ stepId: 1, body });
          dispatch(addSendedStep(step));
        },
      },
    ],
  };
};

export default representativeStep;
